/**
 * PushRegistrationBoot (R8.2).
 *
 * Invisible component mounted inside AppShellPage (authenticated area only).
 *
 * Responsibilities:
 *  1. On mount: if native AND push preference is not disabled, attempt registration.
 *  2. Always: set up persistent listeners for foreground notifications and tap actions.
 *  3. Foreground notification → show MUI Snackbar.
 *  4. Tap on notification (warm start) → navigate directly.
 *  5. Tap on notification (cold start) → store in pendingDeepLink (consumed by DeepLinkNavigator).
 *
 * COLD START RACE CONDITION PREVENTION (Task 4):
 *   On cold start, pushNotificationActionPerformed fires BEFORE React Router is ready.
 *   The listener ALWAYS calls setPendingDeepLink() first.
 *   DeepLinkNavigator (mounted in App.tsx) watches auth state and consumes the link
 *   once auth has resolved.
 *
 * WEB SAFETY: isNative() is checked before ALL push plugin calls.
 * The component renders null on web — no side effects.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNative } from '../platform/isNative';
import {
  requestPushPermission,
  registerForPush,
  getDeviceId,
  getPushPlatform,
  addPushListeners,
  type PushNotificationSchema,
  type ActionPerformed,
} from '../platform/push';
import { AppPreferences } from '../platform/preferences';
import { pushApi } from '../api/push';
import { buildDeepLinkFromData, setPendingDeepLink } from '../platform/pendingDeepLink';
import { useSnackbar } from '../context/SnackbarContext';

const PREF_PUSH_ENABLED = 'cantir.push.enabled';
const PREF_DEVICE_ID = 'cantir.push.deviceId';

export function PushRegistrationBoot() {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  // Use a ref to hold the navigate function so the listener closure is stable
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    // ── Web safety guard ─────────────────────────────────────────────────────
    if (!isNative()) return;

    let cleanupListeners: (() => void) | null = null;

    // ── Step 1: Set up push listeners ────────────────────────────────────────
    // Listeners are set up regardless of registration state so that tap events
    // on warm starts (app backgrounded, not cold-started) are always handled.
    const setupListeners = async () => {
      const cleanup = await addPushListeners({
        onReceived: handleForegroundNotification,
        onActionPerformed: handleNotificationTap,
      });
      cleanupListeners = cleanup;
    };

    // ── Step 2: Attempt registration ─────────────────────────────────────────
    const attemptRegistration = async () => {
      const prefValue = await AppPreferences.get(PREF_PUSH_ENABLED);
      // 'false' means explicitly disabled by the user — respect it
      if (prefValue === 'false') return;

      const permission = await requestPushPermission();
      if (permission !== 'granted') {
        // User denied — mark as disabled so we don't prompt again
        await AppPreferences.set(PREF_PUSH_ENABLED, 'false');
        return;
      }

      try {
        const [token, deviceId] = await Promise.all([
          registerForPush(),
          getDeviceId(),
        ]);

        await pushApi.register({
          deviceId,
          platform: getPushPlatform(),
          pushToken: token,
        });

        await AppPreferences.set(PREF_PUSH_ENABLED, 'true');
        await AppPreferences.set(PREF_DEVICE_ID, deviceId);
      } catch {
        // Registration failure is non-fatal — app still works without push
      }
    };

    setupListeners();
    attemptRegistration();

    return () => {
      cleanupListeners?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Foreground notification handler ──────────────────────────────────────────
  const handleForegroundNotification = (notification: PushNotificationSchema) => {
    const title = notification.title ?? 'Nuova notifica';
    const body = notification.body ? ` — ${notification.body}` : '';
    showMessage(`${title}${body}`, 'info');
  };

  // ── Tap / action performed handler ───────────────────────────────────────────
  //
  // Called for both warm starts and cold starts.
  // We ALWAYS call setPendingDeepLink() first to handle the cold-start case.
  // DeepLinkNavigator will consume the link once auth is fully resolved.
  // For warm starts (React Router already ready), the navigate() call below fires
  // immediately, and DeepLinkNavigator will find the link already consumed.
  const handleNotificationTap = (action: ActionPerformed) => {
    const data = action.notification.data ?? {};
    const link = buildDeepLinkFromData(
      data.resourceType as string | undefined,
      data.resourceId as string | undefined,
    );
    if (!link) return;

    // Store for cold-start safety
    setPendingDeepLink(link);

    // Attempt immediate navigation (works on warm start; no-op if router not ready)
    try {
      navigateRef.current(link, { replace: false });
    } catch {
      // Router not ready yet — DeepLinkNavigator will handle it
    }
  };

  // Renders nothing — this component is a side-effect-only manager
  return null;
}
